/**
 * globo-grid-bridge.js — puente entre la grilla de la app Globo y el theme.
 *
 * La grilla de colecciones y el buscador los renderiza Globo con su propio motor
 * (snippets/globo.filter.product.liquid esta envuelto en {% raw %}: los filtros de
 * adentro NO los ejecuta Shopify).
 *
 * El boton de agregar al carrito de la card es un <form> POST plano, ajeno a los custom
 * elements del theme (<product-form> ni siquiera esta registrado en las paginas de
 * coleccion: product.js no se carga ahi). Sin esto, agregar desde la grilla hace
 * POST -> 302 -> navegacion completa a /cart, sin cart drawer.
 *
 * Idempotente y sin dependencias: app.js carga en todas las paginas. El submit va por
 * delegacion, asi que sobrevive a que Globo redibuje la grilla al filtrar u ordenar.
 *
 * NO reformatea los precios de la card, y no tiene que hacerlo.
 *
 * El separador de miles ya se corrige en la plantilla de Globo con un filtro Liquid
 * (`| money | replace: ',', '.'` en globo.filter.product.liquid). Verificado en vivo
 * el 2026-07-29 sobre /collections/all: AR renderiza "$1.092.630". En la rama `us`
 * ese replace NO va y no es un olvido: USD se escribe "$2,180.00", que es exactamente
 * lo que Globo emite sin tocar nada.
 *
 * Queda desmentida de paso la premisa vieja de que "el formato no sale de
 * shop.money_format". Si sale de ahi: el valor es "${{amount_no_decimals}}" y Shopify
 * elige el separador segun la MONEDA de la tienda, no segun el nombre del placeholder
 * -por eso la ficha de producto en ARS imprime punto con el mismo money_format-.
 *
 * Lo que SI sigue vigente como advertencia: el motor de Globo entrega el precio en
 * UNIDADES DE MONEDA, no en centavos (`{{ variant.price }}` -> 4378742, no
 * 437874200), mientras que `formatMoney` del theme espera centavos. Pasar uno al otro
 * ya rompio una vez (revertido en f7637d6): la grilla anunciaba $43.787 para un
 * producto de $4.378.742 y el carrito cobraba el precio real. Por eso el arreglo es
 * un replace de SEPARADORES sobre los digitos ya renderizados y nunca un recalculo.
 */
(() => {
  'use strict';

  const SECTIONS = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer--inner' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
  ];

  /* -------------------------------------------------- agregar al carrito */

  const renderContents = (parsedState) => {
    SECTIONS.forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target) return; // en coleccion no existe #Cart (main-cart): se saltea
      const sourceHtml = parsedState.sections && parsedState.sections[section.section];
      if (!sourceHtml) return;

      const inner = new DOMParser()
        .parseFromString(sourceHtml, 'text/html')
        .querySelector(section.selector);
      if (!inner) return;

      const elementToReplace = target.querySelector(section.selector) || target;
      // innerHTML es EXCEPCION consciente a la regla del proyecto (ver docs/frontend.md):
      // el dato es HTML de la Section Rendering API de Shopify, no texto, asi que
      // textContent no aplica. Y NO cambiar por replaceChildren(...inner.childNodes):
      // innerHTML nunca ejecuta <script>, pero mover nodos de DOMParser al documento
      // vivo SI los ejecuta -- seria abrir un vector que hoy no existe.
      elementToReplace.innerHTML = inner.innerHTML;
    });

    const drawer = document.getElementById('Cart-Drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    drawer.open();
    if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
    return true;
  };

  const setLoading = (form, loading) => {
    const button = form.querySelector('.spf-product__form-btn-addtocart');
    if (!button) return;
    button.classList.toggle('loading', loading);
    if (loading) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  };

  const onSubmit = (event) => {
    const form = event.target.closest('.spf-product__form');
    if (!form) return;

    // Si el drawer esta apagado en el editor, el POST nativo a /cart es el
    // comportamiento correcto: no hay drawer que abrir.
    if (!document.getElementById('Cart-Drawer')) return;
    if (window.theme && window.theme.settings && window.theme.settings.cart_drawer === false) return;
    if (typeof theme === 'undefined' || !theme.routes || !theme.routes.cart_add_url) return;

    event.preventDefault();
    if (form.dataset.submitting === 'true') return; // dos toques rapidos = dos unidades
    form.dataset.submitting = 'true';
    setLoading(form, true);

    // Deja que el POST nativo haga su trabajo: perder el drawer es feo, perder la
    // venta es peor. Se llama a submit() del prototipo porque el form puede tener
    // un input llamado "submit" que pisa el metodo.
    const fallbackToNativePost = () => {
      HTMLFormElement.prototype.submit.call(form);
    };

    const formData = new FormData(form);
    formData.append('sections', SECTIONS.map((section) => section.section).join(','));
    formData.append('sections_url', window.location.pathname);

    fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body: formData
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // 422 y amigos: la variante no se pudo agregar (sin stock, limite por pedido).
          // El form de Globo no tiene donde mostrar un error, asi que se deja que el
          // POST nativo lleve a /cart, que si muestra el mensaje de Shopify.
          fallbackToNativePost();
          return;
        }
        if (!renderContents(response)) {
          fallbackToNativePost();
          return;
        }
        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: Object.prototype.hasOwnProperty.call(response, 'items') ? response.items[0] : response
          });
        }
        form.dataset.submitting = 'false';
        setLoading(form, false);
      })
      .catch(() => {
        // Red caida, 429, JSON roto: sin esto el toque no hacia nada y el cliente
        // se quedaba mirando una card muerta.
        fallbackToNativePost();
      });
  };

  /* ------------------------------------- Escape cierra el panel de filtros */

  /**
   * El panel de filtros de Globo no cierra con Escape (verificado en el preview el
   * 2026-07-29): el cart drawer del theme si, asi que el sitio se comportaba de dos
   * maneras distintas ante la misma tecla. Quien abre el panel con teclado quedaba
   * sin salida obvia.
   *
   * No se toca el JS de la app: se le da un empujon a su propio boton de cierre.
   * Todo esta escrito para degradar sin ruido — si Globo renombra la clase, el
   * selector no encuentra nada y Escape simplemente no hace nada, como antes.
   */
  const onEscape = (event) => {
    if (event.key !== 'Escape') return;

    // Globo marca el panel abierto con `offcanvas-open` en <html>. Es la unica senal
    // fiable: el boton de cierre esta REPETIDO en el DOM y medir el primero daba
    // falsos negativos (reportaba cerrado con el panel abierto en pantalla).
    const root = document.documentElement;
    if (!root.classList.contains('offcanvas-open')) return;

    // Con el cart drawer abierto el Escape es suyo: cerrarlo es su responsabilidad
    // y no queremos cerrar dos cosas de un saque. Cuando esta cerrado lleva `inert`.
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer && !cartDrawer.hasAttribute('inert')) return;

    // Primero los botones de cierre de la propia app, para que corra su limpieza.
    [...document.querySelectorAll('.gf-close-canvas')]
      .filter((boton) => boton.getBoundingClientRect().width > 0)
      .forEach((boton) => boton.click());

    // Y si la app no reacciono, se saca la clase de estado: es lo que efectivamente
    // cierra el panel. Verificado en el preview de AR el 2026-07-29.
    if (root.classList.contains('offcanvas-open')) root.classList.remove('offcanvas-open');

    // Devolver el foco al disparador, igual que hace el cart drawer. Sin esto el foco
    // queda en un panel que ya no existe y el siguiente Tab arranca desde el principio.
    const toggle = document.querySelector('.gf-refine-toggle-mobile button, .gf-refine-toggle-mobile [role="button"], .gf-refine-toggle-mobile');
    if (toggle && typeof toggle.focus === 'function') toggle.focus();
  };

  /* ------------------------------------------------------------------ init */

  const init = () => {
    if (document.body.dataset.globoBridgeInit === 'true') return;
    document.body.dataset.globoBridgeInit = 'true';

    // Delegacion: sobrevive a que Globo redibuje la grilla al filtrar u ordenar.
    document.addEventListener('submit', onSubmit);
    document.addEventListener('keydown', onEscape);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
